import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { format } from 'date-fns';
import { orderBy, noop } from 'lodash-es';
import { Page, TableList, PageEmptyState, PageErrorState, Button, PageContentHeader, Typography, ConfirmationModal, DropdownMenu, DropdownMenuItem, FilterPickerGroup } from 'seagull';
import { ChevronDown, Download, Import, Plus } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import AccountantAccessLearnMoreButton from '~/components/AccountantAccessLearnMoreButton';
import { EditContactModal } from '~/components/EditContactModal/EditContactModal';
import { ManageContactModalForVendor } from '~/components/ManageContactModal';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useEventReporter, useIoContext, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useEnterAccountantAccess } from '~/hooks/useEnterAccountantAccess';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { usePendingContactEmailUpdates } from '~/hooks/usePendingContactEmailUpdates';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ContactModel, ContactStatus } from '~/models/ContactModel';
import { BusinessChannelEventBinder, PusherEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import ContactsBulkActionResultModal from './ContactsBulkActionResultModal';
import ContactsBulkActions from './ContactsBulkActions';
import styles from './ContactsDashboard.module.scss';
import { contactBiData } from './ContactsDashboard.utils';
import ContactsDashboardRow from './ContactsDashboardRow';
import { EmptyStateIllustration } from './EmptyStateIllustration';
import { useRowSelection } from './hooks/useRowSelection';
import VendorAccountantAccessModal from './VendorAccountantAccessModal';
const sortIds = [
    'companyName',
    'status',
    'primaryContactName',
    'contactDetails'
];
const BULK_EXPORT_CONTACTS_LIMIT = 5000;
const columnDefs = [
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_company_name'
        }
    },
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_status'
        }
    },
    {
        width: [
            170,
            170
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_primary_contact_name'
        }
    },
    {
        width: [
            160,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_contact_details'
        }
    }
];
const singleContactBiEventIds = {
    archive: {
        rowMenuClick: 11617,
        confirm: 11619,
        back: 11621,
        close: 11622,
        success: 11625
    },
    delete: {
        rowMenuClick: 11618,
        confirm: 11620,
        back: 11623,
        close: 11624,
        success: 11626
    }
};
export const importContactsRoute = {
    id: 'import-contacts',
    text: 'Import contacts',
    url: routes.importContacts,
    shouldShow: (param)=>{
        let { isVendor } = param;
        return isVendor;
    }
};
export function ActualContactsDashboard(props) {
    const { data: contacts, isRefreshing, refresh, optimisticUpdate } = props;
    const { FE_CRM } = useFeatureFlags();
    const isMobileDevice = useIsMobileForHomePage();
    const { api, logBI, fetchUserInfo } = useStoreActions();
    const { reportToGtm, reportToAppcues } = useEventReporter();
    const { showErrorToast, showModal, pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const { browser: { downloadCsv } } = useIoContext();
    const [exportingAllCsv, setExportingAllCsv] = useState(false);
    const [bulkExportingCsv, setBulkExportingCsv] = useState(false);
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
    const { urlState: { search, status: contactStatusFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                ContactStatus.Active
            ],
            multi: true
        }
    });
    const hasContacts = !!(contacts && contacts.length);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQboIntegration || showXeroIntegration,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQboIntegration,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXeroIntegration,
        refreshOnHookMount: true
    });
    const importContactsLink = useStore(selectors.filterAvailableRoutes([
        importContactsRoute
    ]))[0];
    const enterAccountantAccess = useEnterAccountantAccess();
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    const { t: contactsDashboardTexts } = useLocaleKeys('pages.contactsDashboard');
    const { t: bulkDeleteConfirmationTexts } = useLocaleKeys('pages.contactsDashboard.bulkDeleteConfirmation');
    const { t: columnHeaderTexts } = useLocaleKeys('pages.contactsDashboard.columnHeaders');
    const { t: columnsTexts } = useLocaleKeys('pages.contactsDashboard.columns');
    const { t: statusFilterTexts } = useLocaleKeys('pages.contactsDashboard.contactStatusFilter');
    const sortOptions = useMemo(()=>[
            {
                id: 'companyName',
                label: FE_CRM ? columnsTexts.clientName() : columnsTexts.companyName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnsTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'primaryContactName',
                label: columnsTexts.contactName(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contactDetails',
                label: columnsTexts.contactDetails(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], [
        FE_CRM
    ]);
    const { t: emptyStateTexts } = useLocaleKeys('pages.contactsDashboard.emptyState');
    const { t: accountantAccessTexts } = useLocaleKeys('pages.contactsDashboard.accountantAccess');
    const headerItems = useMemo(()=>[
            {
                id: 'companyName',
                value: FE_CRM ? columnHeaderTexts.clientName() : columnHeaderTexts.companyName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'primaryContactName',
                value: columnHeaderTexts.contactName()
            },
            {
                id: 'contactDetails',
                value: columnHeaderTexts.contactDetails()
            }
        ], [
        FE_CRM
    ]);
    const contactStatusFilterOptions = useMemo(()=>[
            {
                value: ContactStatus.Active,
                label: statusFilterTexts.active()
            },
            {
                value: ContactStatus.Archived,
                label: statusFilterTexts.archived()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: contactStatusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const statuses = value;
                        return !Array.isArray(statuses) || statuses.length === 0 || statuses.length === contactStatusFilterOptions.length;
                    }
                }
            }
        }), [
        contactStatusFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: contactStatusFilterValue
        }), [
        contactStatusFilterValue
    ]);
    const handleTableFiltersRefresh = (filters, sort)=>{
        const allStatuses = contactStatusFilterOptions.map((opt)=>opt.value);
        const selectedStatus = filters.status;
        const newStatus = (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : allStatuses;
        changeUrlState({
            status: newStatus
        });
        if (sort !== undefined) {
            changeSort(sort);
        }
    };
    const { isPending, markPending, markComplete } = usePendingContactEmailUpdates();
    const onContactUpdated = useCallback((event)=>{
        markComplete(event.payload.contactUpdated.userId);
        refresh();
    }, [
        markComplete,
        refresh
    ]);
    const qboCustomerIdToDisplay = useMemo(()=>{
        if (!qboCustomers) return {};
        return qboCustomers.reduce((prev, qboCustomer)=>{
            prev[qboCustomer.id()] = qboCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        qboCustomers
    ]);
    const xeroCustomerIdToDisplay = useMemo(()=>{
        if (!xeroCustomers) return {};
        return xeroCustomers.reduce((prev, xeroCustomer)=>{
            prev[xeroCustomer.id()] = xeroCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        xeroCustomers
    ]);
    const filteredAndSorted = useMemo(()=>{
        const filtered = (contacts || []).filter((contact)=>{
            // search
            const trimmedSearch = search ? search.trim().toLowerCase() : '';
            const termForSearch = "".concat(contact.companyName(), " ").concat(contact.firstName(), " ").concat(contact.lastName(), " ").concat(contact.email());
            const passesSearch = termForSearch.toLowerCase().includes(trimmedSearch);
            // status filter
            const passesStatusFilter = contactStatusFilterValue.includes(contact.status());
            return passesSearch && passesStatusFilter;
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortPropertiesByHeaderIndex = [
            [
                (c)=>c.companyName()
            ],
            [
                (s)=>s.status(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.firstName(),
                (c)=>c.lastName(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.email(),
                (c)=>c.companyName()
            ]
        ];
        const sortProperties = sortPropertiesByHeaderIndex[colIndex];
        const sortingOrder = sort === 'asc' ? 'desc' : 'asc';
        const orders = Array.from(Array(sortProperties.length)).map(()=>sortingOrder);
        return orderBy(filtered, sortProperties, orders);
    }, [
        contacts,
        tableSort,
        search,
        contactStatusFilterValue
    ]);
    const { selected, numSelected, numVisibleSelected, setSelected, onToggleAllSelection, getSelectedIds, clearSelection } = useRowSelection({
        items: filteredAndSorted
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef
    });
    const onSortChange = (sort)=>{
        logBI(11603, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        changeUrlState({
            search: ''
        });
        debouncedLogSearchToBI.onClear();
    };
    function onClearFilters() {
        changeUrlState({
            search: '',
            status: contactStatusFilterOptions.map((opt)=>opt.value)
        });
    }
    async function handleOptimisticUpdate(param) {
        let { contact, savedContactData } = param;
        if (optimisticUpdate && contact) {
            optimisticUpdate((currentContacts)=>currentContacts.map((c)=>c.id() === savedContactData.id ? new ContactModel(_object_spread_props(_object_spread({}, c.toJSON(), savedContactData.contactData), {
                        version: savedContactData.version
                    })) : c));
        } else {
            await refresh();
        }
    }
    const openContactModal = async (contact)=>{
        logBI(11600, {
            contact: contact && contact.toJSON()
        });
        reportToGtm('button-click', 'contacts-table-view-contact');
        reportToAppcues('button-click', 'contacts-table-view-contact');
        setRegisterSearchBoxHotkey(false);
        try {
            const clientId = contact === null || contact === void 0 ? void 0 : contact.customerId();
            if (FE_CRM && contact != null && clientId != null) {
                const renderEditContactModal = (dismissModal)=>/*#__PURE__*/ _jsx(EditContactModal, {
                        clientId: clientId,
                        contact: contact,
                        lockedClientName: contact.companyName(),
                        markPending: markPending,
                        markComplete: markComplete,
                        dismissModal: dismissModal
                    });
                if (isMobileDevice) {
                    await new Promise((resolve)=>{
                        pushBottomSheetPage({
                            content: renderEditContactModal(()=>{
                                popBottomSheetPage();
                                resolve();
                            })
                        });
                    });
                } else {
                    await showModal({
                        biName: 'edit_contact_modal',
                        closeOnEscapeKey: true,
                        closeOnClickOutside: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return renderEditContactModal(dismissModal);
                        }
                    });
                }
                await refresh();
                return;
            }
            const sharedProps = {
                testId: 'manage-contact-modal',
                existingContact: contact,
                origin: 'Contacts dashboard',
                markPending,
                markComplete
            };
            const renderContactModal = (onClose)=>/*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                    onClose: onClose
                }));
            const savedContactData = isMobileDevice ? await new Promise((resolve)=>{
                pushBottomSheetPage({
                    content: renderContactModal((result)=>{
                        popBottomSheetPage();
                        resolve(result);
                    })
                });
            }) : await showModal({
                biName: 'manage_contact_modal_for_vendor_modal',
                closeOnEscapeKey: true,
                closeOnClickOutside: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return renderContactModal(dismissModal);
                }
            });
            if (savedContactData) {
                handleOptimisticUpdate({
                    contact,
                    savedContactData
                });
            }
        } finally{
            setRegisterSearchBoxHotkey(true);
        }
    };
    const logContactBi = (eventId, contact)=>logBI(eventId, {
            contact: {
                firstName: contact.firstName(),
                lastName: contact.lastName(),
                email: contact.email()
            }
        });
    const { onArchive: onArchiveContact, onRestore: onRestoreContact, onDelete: onDeleteContact } = useContactArchiveRestoreDelete({
        onSuccess: refresh,
        bi: {
            restore: {
                onMenuClick: (contact)=>logContactBi(11605, contact)
            },
            archive: {
                onMenuClick: (contact)=>logBI(singleContactBiEventIds.archive.rowMenuClick, contactBiData(contact)),
                onBack: (contact)=>logBI(singleContactBiEventIds.archive.back, contactBiData(contact)),
                onClose: (contact)=>logBI(singleContactBiEventIds.archive.close, contactBiData(contact)),
                onConfirm: (contact)=>{
                    logBI(singleContactBiEventIds.archive.confirm, contactBiData(contact));
                    logContactBi(11604, contact);
                },
                onSuccess: (contact)=>logBI(singleContactBiEventIds.archive.success, contactBiData(contact))
            },
            delete: {
                onMenuClick: (contact)=>logBI(singleContactBiEventIds.delete.rowMenuClick, contactBiData(contact)),
                onBack: (contact)=>logBI(singleContactBiEventIds.delete.back, contactBiData(contact)),
                onClose: (contact)=>logBI(singleContactBiEventIds.delete.close, contactBiData(contact)),
                onConfirm: (contact)=>logBI(singleContactBiEventIds.delete.confirm, contactBiData(contact)),
                onSuccess: (contact)=>logBI(singleContactBiEventIds.delete.success, contactBiData(contact))
            }
        }
    });
    const onBulkActionComplete = useCallback(()=>{
        clearSelection();
        refresh();
    }, [
        clearSelection,
        refresh
    ]);
    const onBulkArchive = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        await showModal({
            biName: 'contacts_bulk_action_result_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ContactsBulkActionResultModal, {
                    actionType: "archive",
                    contactIds: ids,
                    dismissModal: dismissModal,
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    const onBulkDelete = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        const confirmed = await showModal({
            biName: 'contacts_dashboard_bulk_delete_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'contacts_4_confirm'
                        },
                        secondaryButton: {
                            name: 'contacts_4_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: bulkDeleteConfirmationTexts.title(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: bulkDeleteConfirmationTexts.content({
                            count: ids.length
                        })
                    }),
                    primaryButtonLabel: bulkDeleteConfirmationTexts.primaryButton(),
                    secondaryButtonLabel: bulkDeleteConfirmationTexts.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        await showModal({
            biName: 'contacts_bulk_action_result_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ContactsBulkActionResultModal, {
                    actionType: "delete",
                    contactIds: ids,
                    dismissModal: dismissModal,
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    function onRequestAccountantAccess(contact) {
        showModal({
            biName: 'vendor_accountant_access_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    primaryButtonLabel: accountantAccessTexts.requestAccess(),
                    title: accountantAccessTexts.requestTitle(),
                    primaryButtonTestId: "request-acc-access-submit",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                className: styles.requestAccAccessParagraph,
                                children: accountantAccessTexts.requestDescription()
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: cn(styles.requestAccAccessParagraph, styles.requestAccAccessList),
                                children: [
                                    accountantAccessTexts.requestNotice1(),
                                    accountantAccessTexts.requestNotice2(),
                                    accountantAccessTexts.requestNotice3()
                                ].map((text, index)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            children: text
                                        })
                                    }, index))
                            }),
                            /*#__PURE__*/ _jsx(AccountantAccessLearnMoreButton, {
                                isVendor: true
                            })
                        ]
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.requestFailure(),
                        testId: 'acc-access-request-failure'
                    },
                    onSuccess: refresh,
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13100, {
                            customerId,
                            companyName
                        });
                        await api.accountants.requestAccess(customerId);
                    }
                });
            }
        });
    }
    function onCancelAccountantAccessRequest(contact) {
        showModal({
            biName: 'vendor_accountant_access_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    skin: "danger",
                    primaryButtonLabel: accountantAccessTexts.cancelRequest(),
                    title: accountantAccessTexts.cancelTitle(),
                    primaryButtonTestId: "cancel-acc-access-request-submit",
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: accountantAccessTexts.cancelConfirm({
                            companyName: contact.companyName()
                        })
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.cancelFailure(),
                        testId: 'cancel-acc-access-request-failure'
                    },
                    onSuccess: refresh,
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13103, {
                            customerId,
                            companyName
                        });
                        await api.accountants.cancelRequestAccess(customerId);
                    }
                });
            }
        });
    }
    function onRemoveAccountantAccess(contact) {
        showModal({
            biName: 'vendor_accountant_access_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    skin: "danger",
                    primaryButtonLabel: accountantAccessTexts.removeAccess(),
                    title: accountantAccessTexts.removeTitle(),
                    primaryButtonTestId: "remove-acc-access-submit",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: accountantAccessTexts.removeConfirm({
                                    companyName: contact.companyName()
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: accountantAccessTexts.removeNotice()
                            })
                        ]
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.removeFailure(),
                        testId: 'remove-acc-access-failure'
                    },
                    // We fetchUserInfo to remove the accountant workspace from userInfo.workspaces
                    onSuccess: ()=>Promise.all([
                            refresh(),
                            fetchUserInfo()
                        ]),
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13104, {
                            customerId,
                            companyName
                        });
                        await api.accountants.removeMe(customerId);
                    }
                });
            }
        });
    }
    const importContactsButton = importContactsLink && /*#__PURE__*/ _jsx(Button, {
        bi: {
            name: 'import_contacts'
        },
        skin: "primaryOutline",
        as: Link,
        to: importContactsLink.url,
        testId: "goto-".concat(importContactsLink.id),
        children: contactsDashboardTexts.importContacts()
    });
    async function exportAllContactsCsv() {
        if (exportingAllCsv) return;
        setExportingAllCsv(true);
        try {
            const csvString = await api.contact.exportAllContactsCsv();
            downloadCsv(csvString, "anchor_contacts_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            showErrorToast(contactsDashboardTexts.exportError(), 'export-contacts-csv-error');
        } finally{
            setExportingAllCsv(false);
        }
    }
    async function bulkExportContactsCsv() {
        if (bulkExportingCsv) return;
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        if (ids.length > BULK_EXPORT_CONTACTS_LIMIT) {
            showErrorToast(contactsDashboardTexts.exportLimitExceeded({
                limit: BULK_EXPORT_CONTACTS_LIMIT,
                selected: ids.length
            }), 'export-contacts-csv-limit-exceeded');
            return;
        }
        setBulkExportingCsv(true);
        try {
            const csvString = await api.contact.exportContactsCsvByIds(ids);
            downloadCsv(csvString, "anchor_contacts_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            showErrorToast(contactsDashboardTexts.exportError(), 'export-contacts-csv-error');
        } finally{
            setBulkExportingCsv(false);
        }
    }
    const moreActionsMenu = /*#__PURE__*/ _jsxs(DropdownMenu, {
        target: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'open_contacts_more_actions'
            },
            skin: "primaryOutline",
            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
            testId: "contacts-dashboard-more-actions-btn",
            children: contactsDashboardTexts.moreActions()
        }),
        children: [
            importContactsLink && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'import_contacts'
                },
                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                testId: "goto-".concat(importContactsLink.id),
                as: Link,
                to: importContactsLink.url,
                children: contactsDashboardTexts.importContacts()
            }),
            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'export_all_contacts'
                },
                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                testId: "export-all-contacts-btn",
                disabled: exportingAllCsv,
                intent: Intent.ExportContactsToCsv,
                onClick: exportAllContactsCsv,
                children: contactsDashboardTexts.exportAll()
            })
        ]
    });
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: "contacts-dashboard",
            header: contactsDashboardTexts.title(),
            loading: isRefreshing,
            variant: "containedHeight",
            actions: FE_CRM ? undefined : importContactsButton,
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: "empty-state-page",
                illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                message: emptyStateTexts.noContactsMessage(),
                actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                    testId: "contacts-empty-state-new-btn",
                    text: emptyStateTexts.createAgreement()
                })
            })
        });
    const renderContactsPage = ()=>/*#__PURE__*/ _jsxs(Page, {
            testId: "contacts-dashboard",
            ref: scrollElRef,
            header: contactsDashboardTexts.title(),
            subheader: contactsDashboardTexts.subheader(),
            actions: FE_CRM ? undefined : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    moreActionsMenu,
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'create_contact'
                        },
                        testId: "create-contact-btn",
                        prefixIcon: /*#__PURE__*/ _jsx(Plus, {
                            size: 18
                        }),
                        onClick: ()=>openContactModal(),
                        children: contactsDashboardTexts.createContact()
                    })
                ]
            }),
            loading: isRefreshing,
            onScroll: onScroll,
            children: [
                /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                    source: "mgmt",
                    eventName: PusherEvent.ContactUpdated,
                    callback: onContactUpdated
                }),
                /*#__PURE__*/ _jsxs(TableList.Provider, {
                    hasActionColumn: true,
                    columnDefs: columnDefs,
                    sort: tableSort,
                    onSortChange: onSortChange,
                    enableRowSelection: !FE_CRM,
                    selectedRows: selected,
                    onSelectionChange: setSelected,
                    numRows: filteredAndSorted.length,
                    numSelectedRows: numVisibleSelected,
                    onToggleAllSelection: onToggleAllSelection,
                    children: [
                        /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                    style: {
                                        marginBottom: 24
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: "contacts-dashboard",
                                            filterLabels: filterLabels,
                                            filterConfig: filterConfig,
                                            selectedFilters: selectedFilters,
                                            setSelectedFilters: noop,
                                            onRefresh: handleTableFiltersRefresh,
                                            search: {
                                                bi: {
                                                    name: 'contacts_search'
                                                },
                                                value: search,
                                                testId: 'contacts-dashboard-search',
                                                registerHotkey: registerSearchBoxHotkey,
                                                onChange: (event)=>{
                                                    const searchStr = event.target.value;
                                                    changeUrlState({
                                                        search: searchStr
                                                    });
                                                    debouncedLogSearchToBI.onChange(searchStr);
                                                },
                                                onClear: onClearSearch
                                            },
                                            mobile: {
                                                sortOptions
                                            }
                                        }),
                                        !isMobileDevice && !FE_CRM && /*#__PURE__*/ _jsx(ContactsBulkActions, {
                                            numSelected: numSelected,
                                            onBulkArchive: onBulkArchive,
                                            onBulkDelete: onBulkDelete,
                                            onBulkExport: bulkExportContactsCsv,
                                            bulkExportDisabled: bulkExportingCsv
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    testId: "contacts-dashboard-table-header"
                                })
                            ]
                        }),
                        filteredAndSorted.length ? paginatedData.map((contact)=>/*#__PURE__*/ _jsx(ContactsDashboardRow, {
                                contact: contact,
                                isQboIntegrated: showQboIntegration,
                                isXeroIntegrated: showXeroIntegration,
                                qboCustomerIdToDisplay: qboCustomerIdToDisplay,
                                xeroCustomerIdToDisplay: xeroCustomerIdToDisplay,
                                openContactModal: openContactModal,
                                onRequestAccountantAccess: onRequestAccountantAccess,
                                onCancelAccountantAccessRequest: onCancelAccountantAccessRequest,
                                onRemoveAccountantAccess: onRemoveAccountantAccess,
                                onGotoClientPortal: (c)=>enterAccountantAccess(c.customerId(), c.companyName()),
                                onRestoreContact: onRestoreContact,
                                onArchiveContact: onArchiveContact,
                                onDeleteContact: onDeleteContact,
                                isUpdateInProgress: !!contact.userId() && isPending(contact.userId())
                            }, contact.id())) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                            title: emptyStateTexts.noMatchTitle(),
                            message: emptyStateTexts.noMatchMessage(),
                            actions: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'clear_contacts_filters'
                                },
                                testId: "contacts-dashboard-empty-search-clear-btn",
                                onClick: onClearFilters,
                                children: emptyStateTexts.clearFilters()
                            })
                        })
                    ]
                })
            ]
        });
    return hasContacts ? renderContactsPage() : renderEmptyPage();
}
export function ContactsDashboard() {
    const { contacts, isLoading, isFetching, refresh, optimisticUpdate, error } = useContacts({
        refreshOnMount: true
    });
    const { t: contactsTexts } = useLocaleKeys('pages.contactsDashboard');
    useSetDocumentTitle(contactsTexts.title());
    const handleRefresh = async ()=>{
        const result = await refresh();
        return result.data;
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: contactsTexts.title()
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: "error-state-page",
            title: contactsTexts.title(),
            onRefresh: handleRefresh
        });
    }
    return /*#__PURE__*/ _jsx(ActualContactsDashboard, {
        data: contacts,
        isRefreshing: isFetching,
        refresh: handleRefresh,
        optimisticUpdate: optimisticUpdate
    });
}
